import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation, NavLink, useNavigate } from "react-router-dom";

import toast, { Toaster } from "react-hot-toast";

import logoName from "../assets/logo.png";
import { auth, authStateKnown } from "../helper/firebaseClient";
import Loading from "../components/Loading";
import { api } from "../utils/axios-instance";
import useAuth from "../stores/authStore";
import { orgAdminNeedsCheckout } from "../components/ProtectedRoute";
import { loadRazorpay } from "../utils/razorpay";
import {
  fetchAutopayOptions,
  setupAutopayMandate,
  apiError,
} from "../utils/autopay";
import AutopayOptions, {
  DEFAULT_AUTOPAY_VALUE,
  validateAutopaySelection,
  bankPayload,
} from "../components/autopay/AutopayOptions";

// Display-only prices for the legacy seat-upgrade flow.
// The backend remains authoritative for the actual order amount.
const PRICE_MAP = {
  starter: {
    monthly: 399,
    yearly: 4788,
  },
  basic: {
    monthly: 699,
    yearly: 8388,
  },
  pro: {
    monthly: 1499,
    yearly: 17988,
  },

  // Test plans: keep these only if enabled in your backend.
  starter_test: { monthly: 1, yearly: 12 },
  basic_test: { monthly: 1, yearly: 12 },
  pro_test: { monthly: 1, yearly: 12 },
};

const VALID_BILLING_CYCLES = ["monthly", "yearly"];

function formatRupees(value) {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return `₹${amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function getErrorMessage(error, fallback) {
  const responseData = error?.response?.data;

  if (typeof responseData?.error === "string") {
    return responseData.error;
  }

  if (typeof responseData?.detail === "string") {
    return responseData.detail;
  }

  return fallback;
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  // A checkout payload has explicit plan details when any of these
  // fields is supplied. An organization ID alone can still represent
  // a direct renewal entry and may be hydrated from the server.
  const hasPlanFields = [
    state.planCode,
    state.plan_code,
    state.billingCycle,
    state.billing_cycle,
    state.seats,
  ].some((value) => value !== undefined && value !== null);

  const hasExplicitCheckout = hasPlanFields;

  const [hydratedCheckout, setHydratedCheckout] = useState(null);

  const [checkoutStatus, setCheckoutStatus] = useState(
    hasExplicitCheckout ? "ready" : "loading",
  );

  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const [quoteState, setQuoteState] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  // AutoPay choice (available on every checkout: new org, renewal after
  // expiry, plan change, seat top-up).
  const [autopayValue, setAutopayValue] = useState(DEFAULT_AUTOPAY_VALUE);
  const [autopayOptions, setAutopayOptions] = useState(null);
  const [autopayOptionsLoading, setAutopayOptionsLoading] = useState(false);
  const [autopayOptionsError, setAutopayOptionsError] = useState("");
  const [finalizingStage, setFinalizingStage] = useState("payment");

  // Prevent rapid repeated clicks before React updates the UI.
  const paymentLock = useRef(false);

  const profileData = useAuth((s) => s.profileData);
  const profileFetchedAt = useAuth((s) => s.profileFetchedAt);
  const fetchProfileData = useAuth((s) => s.fetchProfileData);

  // /checkout is NOT behind <ProtectedRoute>, and browsers on iPad/mobile
  // reopen the last URL. So a device that was sitting on /checkout while the
  // subscription was expired would keep showing it even after the customer
  // renewed from another device. For the "no explicit plan" entry (the one
  // ProtectedRoute redirects to), always pull a fresh profile on mount...
  useEffect(() => {
    if (hasExplicitCheckout) return;
    fetchProfileData({ silent: true });
  }, [hasExplicitCheckout, fetchProfileData]);

  // ...and leave as soon as SERVER-VERIFIED data (profileFetchedAt > 0) says
  // this org_admin no longer needs to pay. This also fires when App.js
  // refreshes the profile after the tab is resumed from the background.
  // Explicit checkouts (seat upgrade / plan change from Settings) are for
  // already-active subscriptions, so they are never redirected.
  useEffect(() => {
    if (hasExplicitCheckout || paymentProcessing) return;
    if (!profileFetchedAt || !profileData) return;
    if (profileData.role_priv !== "org_admin") return;

    if (!orgAdminNeedsCheckout(profileData)) {
      navigate("/dashboard", { replace: true });
    }
  }, [
    hasExplicitCheckout,
    paymentProcessing,
    profileFetchedAt,
    profileData,
    navigate,
  ]);

  // Do not rely on a partial location.state object. If the route
  // supplied explicit checkout fields, validate all of them.
  const checkout = useMemo(() => {
    if (hasExplicitCheckout) {
      return {
        orgId: state.orgId ?? state.org_id,
        planCode: state.planCode ?? state.plan_code,
        billingCycle: state.billingCycle ?? state.billing_cycle,
        seats: state.seats,
        isRenewal: state.isRenewal === true,
      };
    }

    return hydratedCheckout;
  }, [hasExplicitCheckout, state, hydratedCheckout]);

  const orgId = checkout?.orgId;
  const planCode = checkout?.planCode;
  const billingCycle = checkout?.billingCycle;
  const seats = checkout?.seats;
  const isRenewal = checkout?.isRenewal === true;

  // A direct renewal entry is hydrated from the user's current
  // subscription, not from arbitrary client-side data.
  const isResubscribe = !hasExplicitCheckout && hydratedCheckout !== null;

  const numericSeats = Number(seats);

  const validCheckout =
    typeof orgId === "string" &&
    orgId.trim().length > 0 &&
    typeof planCode === "string" &&
    planCode.trim().length > 0 &&
    VALID_BILLING_CYCLES.includes(billingCycle) &&
    Number.isSafeInteger(numericSeats) &&
    numericSeats > 0;

  // Load current subscription only when the route did not supply
  // any plan-related fields. If a supplied organization ID exists,
  // ensure the server response belongs to that same organization.
  useEffect(() => {
    if (hasExplicitCheckout) {
      setCheckoutStatus("ready");
      return undefined;
    }

    let cancelled = false;

    async function hydrateCheckout() {
      setCheckoutStatus("loading");
      setHydratedCheckout(null);

      try {
        // On a page reload Firebase restores the session asynchronously, so
        // auth.currentUser can still be null here. Wait for it first.
        await authStateKnown;
        const user = auth.currentUser;

        if (!user) {
          throw new Error("Please log in again.");
        }

        const token = await user.getIdToken();

        const { data } = await api.get("/payments/current/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;

        const paidSeats = Number(data?.seats?.paid);

        const expectedOrgId = state.orgId ?? state.org_id;

        if (expectedOrgId && expectedOrgId !== data?.orgId) {
          throw new Error(
            "The current subscription does not match this organization.",
          );
        }

        if (
          typeof data?.orgId !== "string" ||
          !data.orgId.trim() ||
          typeof data?.planCode !== "string" ||
          !data.planCode.trim() ||
          !VALID_BILLING_CYCLES.includes(data?.billingCycle) ||
          !Number.isSafeInteger(paidSeats) ||
          paidSeats <= 0
        ) {
          throw new Error("No valid subscription was found for renewal.");
        }

        setHydratedCheckout({
          orgId: data.orgId,
          planCode: data.planCode,
          billingCycle: data.billingCycle,
          seats: paidSeats,
          isRenewal: false,
        });

        setCheckoutStatus("ready");
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load subscription for checkout", error);

          setCheckoutStatus("error");
        }
      }
    }

    hydrateCheckout();

    return () => {
      cancelled = true;
    };
  }, [hasExplicitCheckout, state.orgId, state.org_id]);

  // All hooks, including memoized values and callbacks, must be
  // declared before the conditional rendering below.
  const pricePerSeat = useMemo(() => {
    return PRICE_MAP[planCode]?.[billingCycle] ?? 0;
  }, [planCode, billingCycle]);

  // Avoid showing a stale quote when the plan, cycle, or seats
  // change while a previous request is still in flight.
  const quoteKey = useMemo(
    () => JSON.stringify([orgId, planCode, billingCycle, numericSeats]),
    [orgId, planCode, billingCycle, numericSeats],
  );

  const currentQuote = quoteState?.key === quoteKey ? quoteState.data : null;

  // Legacy seat-upgrade checkout uses the number of seats being
  // added. The quote endpoint expects the new total, so do not
  // submit that legacy value as a total-seat quote.
  const totalAmount = useMemo(() => {
    if (isRenewal) {
      return pricePerSeat * numericSeats;
    }

    if (currentQuote) {
      return Number(currentQuote.amount);
    }

    return null;
  }, [isRenewal, pricePerSeat, numericSeats, currentQuote]);

  const isLargeAmount = Number.isFinite(totalAmount) && totalAmount > 150000;

  const quoteIsNoop = currentQuote?.requestType === "noop";

  const autopaySelectionError = validateAutopaySelection(
    autopayValue,
    autopayOptions,
  );

  const quoteReady =
    isRenewal ||
    (currentQuote !== null &&
      Number.isFinite(Number(currentQuote.amount)) &&
      Number(currentQuote.amount) >= 0);

  const canPay =
    checkoutStatus === "ready" &&
    validCheckout &&
    quoteReady &&
    !quoteLoading &&
    !quoteError &&
    !loading &&
    !paymentProcessing &&
    !quoteIsNoop &&
    !autopaySelectionError &&
    totalAmount !== null &&
    Number.isFinite(totalAmount) &&
    totalAmount > 0;

  // Server-calculated quote for standard checkout and resubscription.
  // The backend quote endpoint must authorize the signed-in user
  // and return the amount in rupees.
  useEffect(() => {
    if (checkoutStatus !== "ready" || !validCheckout || isRenewal) {
      setQuoteState(null);
      setQuoteLoading(false);
      setQuoteError("");
      return undefined;
    }

    let cancelled = false;

    async function fetchQuote() {
      setQuoteState(null);
      setQuoteLoading(true);
      setQuoteError("");

      try {
        // On a page reload Firebase restores the session asynchronously, so
        // auth.currentUser can still be null here. Wait for it first.
        await authStateKnown;
        const user = auth.currentUser;

        if (!user) {
          throw new Error("Please log in again.");
        }

        const token = await user.getIdToken();

        const { data } = await api.post(
          "/payments/quote/",
          {
            plan_code: planCode,
            billing_cycle: billingCycle,
            seats: numericSeats,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (cancelled) return;

        const amount = Number(data?.amount);

        if (!Number.isFinite(amount) || amount < 0 || !data?.requestType) {
          throw new Error("The server returned an invalid price quote.");
        }

        setQuoteState({
          key: quoteKey,
          data,
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Checkout quote failed", error);

          setQuoteError(
            getErrorMessage(
              error,
              "Couldn't calculate the amount due. Please try again.",
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setQuoteLoading(false);
        }
      }
    }

    fetchQuote();

    return () => {
      cancelled = true;
    };
  }, [
    checkoutStatus,
    validCheckout,
    isRenewal,
    planCode,
    billingCycle,
    numericSeats,
    quoteKey,
  ]);

  // Which AutoPay methods can cover the renewal this checkout leads to.
  // Failure here never blocks paying — AutoPay can be set up later.
  useEffect(() => {
    if (checkoutStatus !== "ready" || !validCheckout) return undefined;

    let cancelled = false;
    setAutopayOptionsLoading(true);
    setAutopayOptionsError("");

    fetchAutopayOptions({
      orgId,
      planCode,
      billingCycle,
      seats: numericSeats,
      isSeatTopup: isRenewal,
    })
      .then((data) => {
        if (!cancelled) setAutopayOptions(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("AutoPay options failed", error);
        setAutopayOptions(null);
        setAutopayOptionsError(
          "AutoPay isn't available right now. You can turn it on later from Settings → Subscription.",
        );
      })
      .finally(() => {
        if (!cancelled) setAutopayOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    checkoutStatus,
    validCheckout,
    orgId,
    planCode,
    billingCycle,
    numericSeats,
    isRenewal,
  ]);

  // Runs after the payment is verified. Payment success never depends on
  // this step: access is already granted, and AutoPay can be retried from
  // Settings.
  const setupAutopayAfterPayment = useCallback(async () => {
    if (autopayValue.mode !== "autopay" || !autopayValue.method) return;

    setFinalizingStage("autopay");
    toast("Payment received. Now approve AutoPay to finish.", { icon: "🔁" });

    try {
      const result = await setupAutopayMandate({
        orgId,
        method: autopayValue.method,
        authType: autopayValue.authType,
        bankAccount: bankPayload(autopayValue.bank),
      });

      if (result.status === "active") {
        toast.success(
          "AutoPay is on 🎉 Future renewals will be debited automatically.",
        );
      } else if (result.status === "pending_confirmation") {
        toast(
          "AutoPay submitted. Your bank will confirm it within 2 working days.",
          { icon: "⏳", duration: 7000 },
        );
      } else if (result.status === "cancelled_by_user") {
        toast(
          "Payment complete. AutoPay wasn't set up — turn it on anytime from Settings → Subscription.",
          { duration: 7000 },
        );
      } else {
        toast.error(
          "Payment complete, but your bank didn't approve AutoPay. You can try another method from Settings → Subscription.",
          { duration: 8000 },
        );
      }
    } catch (error) {
      console.error("AutoPay setup failed", error);
      toast.error(
        `${apiError(error, "AutoPay setup failed.")} Your payment is complete; you can set up AutoPay from Settings → Subscription.`,
        { duration: 8000 },
      );
    }
  }, [autopayValue, orgId]);

  const handleReturnHome = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const verifyPayment = useCallback(
    async (response) => {
      try {
        setPaymentProcessing(true);
        setLoading(false);

        // On a page reload Firebase restores the session asynchronously, so
        // auth.currentUser can still be null here. Wait for it first.
        await authStateKnown;
        const user = auth.currentUser;

        if (!user) {
          throw new Error(
            "Your login session expired. Please log in and contact support if you were charged.",
          );
        }

        const token = await user.getIdToken();

        const { data } = await api.post("/payments/verify-payment/", response, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (data?.status !== "success" && data?.status !== "already_verified") {
          throw new Error(
            "Payment verification is not complete. Please contact support before retrying.",
          );
        }

        // Refresh the cached profile (subscription_active / is_approved) on
        // THIS device before navigating, otherwise ProtectedRoute still sees
        // the pre-payment profile and bounces straight back to /checkout.
        await fetchProfileData({ silent: true });

        // Optional second step: register the AutoPay mandate. The org is
        // already active at this point, so this can never cost the customer
        // the payment they just made.
        await setupAutopayAfterPayment();

        if (isRenewal) {
          toast.success(
            "Seats added successfully 🎉 Invitations are being sent.",
          );

          window.setTimeout(() => {
            navigate("/dashboard", { replace: true });
          }, 1200);

          return;
        }

        if (isResubscribe) {
          toast.success("Subscription renewed 🎉");

          window.setTimeout(() => {
            navigate("/dashboard", { replace: true });
          }, 1200);

          return;
        }

        toast.success("Payment successful 🎉 Login to your Dashboard.");

        window.setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1200);
      } catch (error) {
        console.error("Payment verification failed", error);

        setPaymentProcessing(false);

        toast.error(
          getErrorMessage(
            error,
            "Payment verification failed. Please contact support.",
          ),
          { duration: 7000 },
        );
      }
    },
    [
      isRenewal,
      isResubscribe,
      navigate,
      fetchProfileData,
      setupAutopayAfterPayment,
    ],
  );

  const createOrder = useCallback(async () => {
    if (paymentLock.current || !canPay) {
      return;
    }

    paymentLock.current = true;
    setLoading(true);

    try {
      // On a page reload Firebase restores the session asynchronously, so
      // auth.currentUser can still be null here. Wait for it first.
      await authStateKnown;
      const user = auth.currentUser;

      if (!user) {
        throw new Error("Please log in again to continue payment.");
      }

      const loaded = await loadRazorpay();

      if (!loaded || !window.Razorpay) {
        throw new Error(
          "Failed to load the payment gateway. Please check your connection and try again.",
        );
      }

      const token = await user.getIdToken();

      // The backend calculates and validates the final amount.
      // Never use the client-side quote as the order amount.
      const { data } = await api.post(
        "/payments/create-order/",
        {
          plan_code: planCode,
          billing_cycle: billingCycle,
          org_id: orgId,
          seats: numericSeats,
          isRenewal,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const orderAmount = Number(data?.amount_paise);

      if (
        typeof data?.key !== "string" ||
        !data.key.trim() ||
        typeof data?.order_id !== "string" ||
        !data.order_id.trim() ||
        !Number.isSafeInteger(orderAmount) ||
        orderAmount <= 0 ||
        data?.currency !== "INR"
      ) {
        throw new Error(
          "The server returned an invalid payment order. Please contact support.",
        );
      }

      const description = isRenewal
        ? `${planCode.toUpperCase()} Plan — Seat Upgrade`
        : `${planCode.toUpperCase()} Plan`;

      const options = {
        key: data.key,
        order_id: data.order_id,
        amount: orderAmount,
        currency: data.currency,
        name: "TwoKey",
        description,

        config: {
          display: {
            blocks: {
              ...(orderAmount / 100 > 150000
                ? {}
                : {
                    upi: {
                      name: "Pay using UPI",
                      instruments: [{ method: "upi" }],
                    },
                  }),

              other: {
                name: "Other Payment Methods",
                instruments: [
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" },
                  { method: "emi" },
                ],
              },
            },

            sequence:
              orderAmount / 100 > 150000
                ? ["block.other"]
                : ["block.upi", "block.other"],

            preferences: {
              show_default_blocks: false,
            },
          },
        },

        upi: orderAmount / 100 > 150000 ? undefined : { flow: "qr" },

        notes: {
          org_id: orgId,
          plan: planCode,
          seats: String(numericSeats),
        },

        modal: {
          ondismiss: () => {
            setLoading(false);
            paymentLock.current = false;

            toast("Payment cancelled", {
              icon: "⚠️",
            });
          },
        },

        handler: async (response) => {
          // The backend must verify Razorpay's signature and
          // apply the subscription update before we navigate.
          await verifyPayment(response);
        },

        theme: {
          color: "#5E5ADB",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {
        console.error("Razorpay payment failed", response?.error);

        setLoading(false);
        paymentLock.current = false;

        toast.error(
          response?.error?.description || "Payment failed. Please try again.",
        );
      });

      razorpay.open();
      setLoading(false);
    } catch (error) {
      console.error("Failed to create payment order", error);

      setLoading(false);
      paymentLock.current = false;

      toast.error(
        getErrorMessage(error, "Failed to initiate payment. Please try again."),
        { duration: 6000 },
      );
    }
  }, [
    canPay,
    planCode,
    billingCycle,
    orgId,
    numericSeats,
    isRenewal,
    verifyPayment,
  ]);

  // All hooks are declared above this point.
  if (checkoutStatus === "loading") {
    return <Loading message="Loading your subscription..." />;
  }

  if (checkoutStatus === "error") {
    return (
      <div className="min-h-screen bg-[#FAF8F7] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Unable to load checkout</h2>

          <p className="text-gray-600 mb-6">
            We couldn't retrieve a valid subscription for this checkout. Please
            return to the app and try again.
          </p>

          <button
            onClick={handleReturnHome}
            className="w-full bg-[#5E5ADB] text-white py-3 rounded-lg font-medium"
          >
            Return home
          </button>
        </div>
      </div>
    );
  }

  if (!validCheckout) {
    return (
      <div className="min-h-screen bg-[#FAF8F7] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Invalid checkout session</h2>

          <p className="text-gray-600 mb-6">
            Some required checkout details are missing or invalid. Please return
            to the previous page and start checkout again.
          </p>

          <button
            onClick={handleReturnHome}
            className="w-full bg-[#5E5ADB] text-white py-3 rounded-lg font-medium"
          >
            Return home
          </button>
        </div>
      </div>
    );
  }

  if (paymentProcessing) {
    return (
      <>
        <Toaster position="top-center" />
        <Loading
          message={
            finalizingStage === "autopay"
              ? "Setting up AutoPay..."
              : "Finalizing your subscription..."
          }
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F7]">
      <Toaster position="top-center" />

      <nav className="bg-[#FAF8F7] h-[72px] flex items-center px-4 md:px-12 font-tahoma">
        <NavLink
          to="/"
          aria-label="TwoKey home"
          className="flex items-center gap-2 transition-transform duration-200 hover:scale-105"
        >
          <img
            src={logoName}
            alt="TwoKey"
            className="h-8 md:h-10 object-contain"
          />

          <span
            className="text-lg font-[400] tracking-wide"
            style={{
              fontFamily: "Times New Roman, serif",
            }}
          >
            TwoKey
          </span>
        </NavLink>
      </nav>

      <main className="flex items-center justify-center px-4 py-10">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <h2 className="text-2xl font-bold mb-6">Checkout</h2>

          <div className="space-y-4 text-gray-700">
            <div className="flex justify-between gap-4">
              <span>Plan</span>
              <span className="font-medium text-right">
                {planCode.toUpperCase()}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span>Billing</span>
              <span className="capitalize text-right">{billingCycle}</span>
            </div>

            <div className="flex justify-between gap-4">
              <span>Price per seat</span>
              <span className="text-right">
                {currentQuote?.unitPrice != null
                  ? formatRupees(currentQuote.unitPrice)
                  : isRenewal
                    ? formatRupees(pricePerSeat)
                    : quoteLoading
                      ? "Calculating..."
                      : "—"}
                {" / user"}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span>{isRenewal ? "Additional seats" : "Seats"}</span>
              <span className="text-right">{numericSeats}</span>
            </div>

            {currentQuote?.requestType === "arrears" &&
              Number(currentQuote.cyclesOwed) > 1 && (
                <div className="flex justify-between gap-4 text-amber-600 text-sm">
                  <span>Billing cycles owed</span>
                  <span>{currentQuote.cyclesOwed} cycles</span>
                </div>
              )}

            {currentQuote?.requestType === "change" && (
              <>
                <div className="flex justify-between gap-4 text-sm text-gray-500">
                  <span>Days remaining in cycle</span>
                  <span>{currentQuote.remainingDays ?? "—"}</span>
                </div>

                <div className="flex justify-between gap-4 text-sm text-gray-500">
                  <span>Credit for current plan</span>
                  <span>{formatRupees(currentQuote.credit ?? 0)}</span>
                </div>

                <div className="flex justify-between gap-4 text-sm text-gray-500">
                  <span>New plan (remaining days)</span>
                  <span>{formatRupees(currentQuote.charge ?? 0)}</span>
                </div>
              </>
            )}

            <div className="border-t pt-4 flex justify-between gap-4 text-lg font-semibold">
              <span>Total</span>
              <span className="text-right">
                {quoteLoading && !isRenewal
                  ? "Calculating..."
                  : totalAmount === null
                    ? "—"
                    : formatRupees(totalAmount)}
              </span>
            </div>
          </div>

          {!quoteIsNoop && (
            <div className="mt-6 border-t pt-5">
              <AutopayOptions
                options={autopayOptions}
                loading={autopayOptionsLoading}
                error={autopayOptionsError}
                value={autopayValue}
                onChange={setAutopayValue}
                disabled={loading || paymentProcessing}
              />
              {autopayValue.mode === "autopay" && autopaySelectionError && (
                <p className="text-xs text-amber-700 mt-2">
                  {autopaySelectionError}
                </p>
              )}
            </div>
          )}

          {quoteIsNoop && (
            <p className="text-amber-700 text-sm text-center mt-4">
              Your current subscription already matches this selection. No
              payment is required.
            </p>
          )}

          {quoteError && (
            <p role="alert" className="text-red-600 text-sm text-center mt-4">
              {quoteError}
            </p>
          )}

          <button
            type="button"
            onClick={createOrder}
            disabled={!canPay}
            className="w-full mt-8 bg-[#5E5ADB] text-white py-3 rounded-lg font-medium hover:bg-[#4C47C7] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading
              ? "Processing..."
              : paymentProcessing
                ? "Verifying payment..."
                : quoteLoading && !isRenewal
                  ? "Calculating..."
                  : quoteIsNoop
                    ? "No payment required"
                    : totalAmount === null
                      ? "Price unavailable"
                      : autopayValue.mode === "autopay"
                        ? `Pay ${formatRupees(totalAmount)} & set up AutoPay`
                        : `Pay ${formatRupees(totalAmount)}`}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Secure payment • Final amount is confirmed by the server before
            payment.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
